'use client'

import { FormEvent, useState } from 'react'
import { ArrowRight, Check } from 'lucide-react'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void
    clarity?: (...args: unknown[]) => void
  }
}

interface NewsletterFormProps {
  source: string
  buttonLabel?: string
  className?: string
  compact?: boolean
}

type FormState = 'idle' | 'submitting' | 'success' | 'error'

function trackNewsletterEvent(action: string, source: string) {
  window.gtag?.('event', action, {
    event_category: 'newsletter',
    newsletter_source: source,
  })
  window.clarity?.('event', `${action}:${source}`)
  window.dispatchEvent(
    new CustomEvent('allisports:newsletter', {
      detail: { action, source },
    })
  )
}

export default function NewsletterForm({
  source,
  buttonLabel = 'Join the newsletter',
  className = '',
  compact = false,
}: NewsletterFormProps) {
  const [state, setState] = useState<FormState>('idle')
  const [message, setMessage] = useState('')

  async function subscribe(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (state === 'submitting' || state === 'success') return

    const form = event.currentTarget
    const formData = new FormData(form)
    const email = String(formData.get('email') || '').trim()
    const website = String(formData.get('website') || '')

    setState('submitting')
    setMessage('')
    trackNewsletterEvent('newsletter_submit', source)

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source,
          website,
          referrer: window.location.href,
        }),
      })
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Unable to subscribe')
      }

      form.reset()
      setState('success')
      setMessage('You are in. Check your inbox to confirm your subscription.')
      trackNewsletterEvent('newsletter_success', source)
    } catch {
      setState('error')
      setMessage('We could not add you just now. Please try again.')
      trackNewsletterEvent('newsletter_error', source)
    }
  }

  const isSuccess = state === 'success'

  return (
    <div className={className}>
      <form
        onSubmit={subscribe}
        className={`relative flex w-full gap-3 ${compact ? 'flex-col sm:flex-row' : 'flex-col md:flex-row'}`}
      >
        <label className="sr-only" htmlFor={`newsletter-email-${source}`}>
          Email address
        </label>
        <input
          id={`newsletter-email-${source}`}
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          disabled={isSuccess}
          placeholder="Your email address"
          className="h-13 min-w-0 flex-1 rounded-full border border-white/15 bg-black/30 px-5 text-base text-white placeholder:text-white/40 focus:border-[var(--alli-red)] focus:ring-2 focus:ring-[var(--alli-red)]/30 disabled:opacity-60"
        />
        <div className="pointer-events-none absolute -left-[9999px]" aria-hidden="true">
          <label htmlFor={`newsletter-website-${source}`}>Website</label>
          <input
            id={`newsletter-website-${source}`}
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
          />
        </div>
        <button
          type="submit"
          disabled={state === 'submitting' || isSuccess}
          className="inline-flex h-13 shrink-0 items-center justify-center gap-2 rounded-full bg-[var(--alli-red)] px-6 text-sm font-bold text-white transition hover:bg-[var(--alli-red-strong)] disabled:cursor-default disabled:opacity-70"
        >
          {isSuccess ? (
            <>
              Subscribed <Check size={17} />
            </>
          ) : state === 'submitting' ? (
            'Joining...'
          ) : (
            <>
              {buttonLabel} <ArrowRight size={17} />
            </>
          )}
        </button>
      </form>
      <p
        className={`mt-3 min-h-5 text-sm ${state === 'error' ? 'text-[#ff6976]' : 'text-white/55'}`}
        role="status"
        aria-live="polite"
      >
        {message}
      </p>
    </div>
  )
}
